import type { CreateCartPaymentIntentResponse, StripeCardModel } from '../interfaces';
import type { FieldSchema } from '@FormModule';
import { CreateCartPaymentIntentService } from '../services';

interface CardConfirmation {
  error?: string;
  payment_intent_id?: string;
}

// NOTE :: The card is confirmed in the browser. It is not turned into a token. The api builds one
// PaymentIntent per cart. The price comes from the cart in the session. The browser confirms it
// against the card field. If the bank asks for a 3D Secure check, it pops here. Only the resulting
// `payment_intent_id` goes back to the api. A payment that already succeeded comes back with no
// client secret. That is a retry after the finish call failed once. Skip the confirm and finish
// with the same id.
export default function () {
  const CARD_NOT_READY_ERROR = 'components.forms.errors.requiredStripe';

  // HELPERS
  // NOTE :: Use the card that is on screen, not the first model that still holds a Stripe object.
  // After Credit Card is switched to Split, the hidden `stripe` field still holds its old,
  // unmounted card field. Using it fails the confirm and writes the decline onto a field nobody sees.
  function getStripeCardModel(fieldsSchema: FieldSchema[]): StripeCardModel | undefined {
    const stripeField = fieldsSchema.find(({ name }) => name === 'stripe');
    if (stripeField?.settings?.show) return stripeField.model;

    const splitPaymentField = fieldsSchema.find(({ name }) => name === 'split_payment');
    if (splitPaymentField?.settings?.show) return splitPaymentField.model?.stripe_card;

    return stripeField?.model?.stripe ? stripeField.model : splitPaymentField?.model?.stripe_card;
  }

  // NOTE :: Write the confirm error onto the card model. `MFieldStripeCreditCard` watches it and
  // shows the message under the card, so staff see the decline where they typed the card.
  function setStripeCardError(card: StripeCardModel | undefined, message: string) {
    if (!card) return;

    card.stripeError = message;
  }

  // SERVICES
  async function confirmPreparedIntent(
    intent: CreateCartPaymentIntentResponse,
    card: StripeCardModel | undefined
  ): Promise<CardConfirmation> {
    if (intent.status === 'succeeded' || !intent.client_secret) return { payment_intent_id: intent.payment_intent_id };

    const cardIsReady = !!card?.stripe && !!card?.cardElement;
    if (!cardIsReady) return { error: CARD_NOT_READY_ERROR };

    const { error, paymentIntent } = await card!.stripe!.confirmCardPayment(intent.client_secret, {
      payment_method: {
        card: card!.cardElement!,
        billing_details: { name: card!.cardHolderName },
      },
    });

    if (error) return { error: error.message ?? CARD_NOT_READY_ERROR };

    return { payment_intent_id: paymentIntent?.id };
  }

  async function confirmCardPayment(payload: any, card: StripeCardModel | undefined): Promise<CardConfirmation> {
    const { data } = await CreateCartPaymentIntentService(payload);

    return confirmPreparedIntent(data, card);
  }

  return {
    confirmCardPayment,
    confirmPreparedIntent,
    getStripeCardModel,
    setStripeCardError,
  };
}
